/**
 * AdminMembresCreation - Orchestrateur principal
 * Gestion des membres par Super Admin / Admin
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Users, UserPlus, RefreshCw, CheckCircle, Clock, CreditCard
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import MembreCreationForm from '../MembreCreationForm';
import MembresTable from '../MembresTable';
import FTPEPendingBanner from '../FTPEPendingBanner';
import MemberInvitationIA from '../MemberInvitationIA';
import { getToken, setToken as setStoredToken } from '../../services/tokenService';
import { DetailModal, SouscriptionModal, DocumentsModal, FTPEModal, EstimationSimulatorPickerModal } from './MemberModals';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminMembresCreation({ userRole = 'admin' }) {
  const { getAuthHeader } = useAuth();
  const [membres, setMembres] = useState([]);
  const [coopers, setCoopers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);

  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showSouscriptionModal, setShowSouscriptionModal] = useState(false);
  const [showEstimationPickerModal, setShowEstimationPickerModal] = useState(false);
  const [showDocumentsModal, setShowDocumentsModal] = useState(false);
  const [showFTPEModal, setShowFTPEModal] = useState(false);
  const [membreDocuments, setMembreDocuments] = useState(null);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [ftpeLoading, setFtpeLoading] = useState(false);

  const [selectedMembre, setSelectedMembre] = useState(null);
  const [selectedCooper, setSelectedCooper] = useState('');

  const [stats, setStats] = useState({
    total: 0, actifs: 0, en_attente: 0, avec_souscription: 0
  });

  useEffect(() => { fetchData(); }, []);


  const fetchData = async () => {
    setLoading(true);
    try {
      const [membresRes, coopersRes] = await Promise.all([
        axios.get(`${API}/cooper/membres/liste`, { headers: getAuthHeader() }).catch(() => ({ data: { membres: [] } })),
        axios.get(`${API}/coopers`, { headers: getAuthHeader() }).catch(() => ({ data: [] }))
      ]);
      const membresData = (membresRes.data.membres || []).sort((a, b) =>
        new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setMembres(membresData);
      const coopersData = coopersRes.data?.coopers || coopersRes.data || [];
      const sortedCoopers = (Array.isArray(coopersData) ? coopersData : []).sort((a, b) =>
        new Date(b.date_creation || b.created_at || 0) - new Date(a.date_creation || a.created_at || 0));
      setCoopers(sortedCoopers);
      setStats({
        total: membresData.length,
        actifs: membresData.filter(m => m.status === 'actif').length,
        en_attente: membresData.filter(m => m.status === 'en_attente_connexion').length,
        avec_souscription: membresData.filter(m => m.souscription_id).length
      });
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleImpersonate = async (membre) => {
    if (!['admin', 'super_admin'].includes(userRole)) {
      toast.error('Accès refusé. Fonctionnalité réservée aux administrateurs.');
      return;
    }
    try {
      const currentToken = getToken();
      const response = await axios.post(`${API}/admin/impersonate/start/${membre.id}`, {}, { headers: getAuthHeader() });
      if (response.data.success) {
        localStorage.setItem('original_admin_token', currentToken);
        setStoredToken(response.data.token);
        localStorage.setItem('impersonated_user', JSON.stringify(response.data.impersonated_user));
        toast.success(response.data.message);
        window.dispatchEvent(new Event('impersonate-start'));
        const targetRole = response.data.impersonated_user?.role;
        setTimeout(() => {
          const routes = { member: '/dashboard', cooper: '/cooper', expert: '/expert' };
          window.location.href = routes[targetRole] || '/dashboard';
        }, 500);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'impersonation");
    }
  };

  const loadMembreDocuments = async (membreId) => {
    setLoadingDocuments(true);
    try {
      const response = await axios.get(`${API}/cooper/membres/${membreId}/mes-documents`, { headers: getAuthHeader() });
      const data = response.data;
      const now = new Date();
      const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const hasValidUrl = (doc) => {
        const url = doc.url || doc.download_url || doc.file_url || doc.path;
        return url && typeof url === 'string' && url.trim().length > 0;
      };

      // Extrait la date d'échéance d'un document, peu importe le champ utilisé
      // côté backend. Renvoie un objet Date ou null.
      const extractEcheance = (doc) => {
        const raw = doc.date_echeance || doc.date_expiration || doc.date_fin || doc.periode_adhesion_fin;
        if (!raw) return null;
        try {
          if (typeof raw === 'string' && raw.includes('/')) {
            const [d, m, y] = raw.split('/');
            return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
          }
          const dt = new Date(raw);
          return Number.isNaN(dt.getTime()) ? null : dt;
        } catch { return null; }
      };

      // Un contrat signé est ARCHIVÉ si date_echeance + 1 jour < aujourd'hui.
      // (V35.9 — Auto-archivage des contrats échus)
      const isArchived = (doc) => {
        const echeance = extractEcheance(doc);
        if (!echeance) return false; // pas de date → on garde actif
        const archiveAt = new Date(echeance.getTime() + 24 * 60 * 60 * 1000);
        return archiveAt < now;
      };

      const isNotExpired = (doc) => {
        const expDate = doc.date_expiration || doc.date_fin || doc.periode_adhesion_fin;
        if (!expDate) return true;
        try {
          let expDt;
          if (typeof expDate === 'string' && expDate.includes('/')) {
            const [d, m, y] = expDate.split('/');
            expDt = new Date(y, m - 1, d);
          } else {
            expDt = new Date(expDate);
          }
          return expDt >= now;
        } catch { return true; }
      };
      const isWithin30Days = (doc) => {
        const createdAt = doc.date_creation || doc.created_at;
        if (!createdAt) return true;
        try { return new Date(createdAt) >= thirtyDaysAgo; } catch { return true; }
      };
      const _isAvailable = (doc) => hasValidUrl(doc) && isNotExpired(doc);

      // Helpers de catégorisation par type / statut de signature
      const isRcdsAttestation = (d) => {
        const t = (d.type || '').toLowerCase();
        return t.includes('attestation') && t.includes('rcds');
      };
      const isFtpeAttestation = (d) => {
        const t = (d.type || '').toLowerCase();
        return t.includes('ftpe') || t.includes('attestation_ftpe');
      };
      const isPackConditions = (d) => {
        const t = (d.type || '').toLowerCase();
        return t === 'conditions_generales' || t === 'conditions_generales_particulieres' || t.includes('pack2') || t === 'avenant';
      };
      const isSigned = (d) => d.document_status === 'signee';

      // Deduplication: versions admin signees remplacent les versions systeme
      const adminDocs = (data.documents_admin || []);
      const adminTypes = new Set(adminDocs.map(d => d.type));
      const ADMIN_SUPERSEDES = {
        'attestation_signee': ['attestation_rcds'],
        'conditions_generales_particulieres_signees': ['conditions_generales'],
        'attestation_ftpe_signee': ['attestation_ftpe'],
        'devis_ess': ['devis_ess'],
      };
      const supersededTypes = new Set();
      for (const [adminType, sysTypes] of Object.entries(ADMIN_SUPERSEDES)) {
        if (adminTypes.has(adminType)) sysTypes.forEach(t => supersededTypes.add(t));
      }
      const systemDocs = (data.documents || []).filter(d => !supersededTypes.has(d.type));

      // === V35.9 — Tri en 3 buckets : signés-actifs / non-signés / archivés ===
      // 1) Attestations RCDS signées et encore valides → "Contrats Signés"
      const attestationsRcdsAll = systemDocs.filter((d) => isRcdsAttestation(d) && hasValidUrl(d));
      const attestations_rcds_signees = attestationsRcdsAll.filter((d) => isSigned(d) && !isArchived(d));
      const attestations_rcds_archivees = attestationsRcdsAll.filter((d) => isSigned(d) && isArchived(d));
      const attestations_rcds_non_signees = attestationsRcdsAll.filter((d) => !isSigned(d));

      // 2) Attestations FTPE — signées + non signées + archivées
      const attestationsFtpeAll = systemDocs.filter((d) => isFtpeAttestation(d));
      const attestations_ftpe_signees = attestationsFtpeAll.filter((d) => isSigned(d) && !isArchived(d));
      const attestations_ftpe_archivees = attestationsFtpeAll.filter((d) => isSigned(d) && isArchived(d));
      const attestations_ftpe_non_signees = attestationsFtpeAll.filter((d) => !isSigned(d));

      // 3) Conditions générales / particulières — séparées signées vs non-signées
      const condAll = systemDocs.filter((d) => isPackConditions(d) && hasValidUrl(d));
      const conditions_signees = condAll.filter((d) => isSigned(d) && !isArchived(d));
      const conditions_archivees = condAll.filter((d) => isSigned(d) && isArchived(d));
      const conditions_non_signees = condAll.filter((d) => !isSigned(d));

      // 4) Documents contractuels "Autres documents" — toutes les versions NON SIGNÉES
      //    (Pack1 Attestation RCDS, Pack2 Conditions, Attestation FTPE non encore signée),
      //    plus devis_ess / propositions (qui restent dans la section dédiée).
      const propositionsEss = systemDocs.filter((d) =>
        ['devis_ess', 'devis_ess_genere', 'proposition'].includes(d.type) && hasValidUrl(d));
      const documents_contractuels = [
        ...attestations_rcds_non_signees,
        ...conditions_non_signees,
        ...attestations_ftpe_non_signees.filter(hasValidUrl),
        ...propositionsEss,
      ];

      const hasFtpeSignee = data.has_ftpe_signee || attestations_ftpe_signees.length > 0;

      // 5) Documents GED & Admin (passent dans archivés s'ils sont échus)
      const documentsGedAll = (data.documents_ged || []).filter(hasValidUrl);
      const documents_ged = documentsGedAll.filter((d) => !isArchived(d));
      const documents_ged_archives = documentsGedAll.filter((d) => isArchived(d));

      setMembreDocuments({
        simulations_en_cours: (data.souscriptions_en_cours || []).filter(sim => isWithin30Days(sim) && sim.jours_restants > 0),
        certificats_adhesion: hasFtpeSignee ? systemDocs.filter(d =>
          d.type === 'certificat_adhesion' || d.type === 'membre_adhesion' || d.type === 'certificat'
        ) : [],
        attestations_rcds_signees,
        attestations_ftpe_signees,
        conditions_signees,
        documents_contractuels,
        recus_paiements: (data.recus_paiements || []).filter(hasValidUrl),
        documents: systemDocs.filter(hasValidUrl),
        documents_ged,
        documents_admin: adminDocs,
        has_ftpe_signee: hasFtpeSignee,
        // === Section "Archivés" (dépliable) ===
        documents_archives: {
          attestations_rcds: attestations_rcds_archivees,
          attestations_ftpe: attestations_ftpe_archivees,
          conditions: conditions_archivees,
          ged: documents_ged_archives,
        },
      });
    } catch {
      setMembreDocuments({
        simulations_en_cours: [], certificats_adhesion: [],
        attestations_rcds_signees: [], attestations_ftpe_signees: [], conditions_signees: [],
        documents_contractuels: [], documents_ged: [], documents_admin: [],
        has_ftpe_signee: false,
        documents_archives: { attestations_rcds: [], attestations_ftpe: [], conditions: [], ged: [] },
      });
    } finally {
      setLoadingDocuments(false);
    }
  };

  const handleFTPEAction = async (action) => {
    if (!selectedMembre) return;
    setFtpeLoading(true);
    try {
      const endpoint = action === 'adhesion' ? 'adhesion' : 'renouvellement';
      await axios.post(`${API}/ftpe/${endpoint}`, { membre_id: selectedMembre.id }, { headers: getAuthHeader() });
      toast.success(action === 'adhesion' ? "Demande d'adhésion FTPE envoyée !" : 'Demande de renouvellement FTPE envoyée !');
      setShowFTPEModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'action FTPE");
    } finally {
      setFtpeLoading(false);
    }
  };

  const handleCreateSouscription = async () => {
    if (!selectedMembre || !selectedCooper) {
      toast.error("Veuillez sélectionner un COOPER'S");
      return;
    }
    try {
      await axios.post(`${API}/souscriptions`, {
        membre_id: selectedMembre.id, cooper_id: selectedCooper, type: 'standard'
      }, { headers: getAuthHeader() });
      toast.success('Souscription créée avec succès !');
      setShowSouscriptionModal(false);
      setSelectedCooper('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const getRoleLabel = () => userRole === 'super_admin' ? 'Super Admin' : 'Admin';

  return (
    <div className="space-y-6" data-testid="admin-membres-creation">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Création de Membres ({getRoleLabel()})</h2>
          <p className="text-gray-600">Créez des comptes membres et gérez leurs souscriptions</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchData} variant="outline" size="sm" data-testid="refresh-members-btn">
            <RefreshCw className="h-4 w-4 mr-2" />Actualiser
          </Button>
          <MemberInvitationIA getAuthHeader={getAuthHeader} onChanged={fetchData} userRole={userRole} />
          <Button onClick={() => setShowCreateModal(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-member-btn">
            <UserPlus className="h-4 w-4 mr-2" />Nouveau Membre
          </Button>
        </div>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Total Membres</p>
                <p className="text-3xl font-bold text-blue-600">{stats.total}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Actifs</p>
                <p className="text-3xl font-bold text-green-600">{stats.actifs}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En Attente</p>
                <p className="text-3xl font-bold text-orange-600">{stats.en_attente}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-orange-100 flex items-center justify-center">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Avec Souscription</p>
                <p className="text-3xl font-bold text-purple-600">{stats.avec_souscription}</p>
              </div>
              <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <FTPEPendingBanner getAuthHeader={getAuthHeader} />

      {/* Liste des membres */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-xl">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Users className="h-5 w-5" />Membres créés
              </CardTitle>
              <CardDescription className="text-indigo-100 mt-1">
                Membres créés par l&apos;administration - Gestion des souscriptions
              </CardDescription>
            </div>
            <Badge variant="secondary" className="bg-slate-700 text-white border-0 text-lg px-3 py-1 font-semibold shadow-md">
              {membres.length}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <MembresTable
            membres={membres}
            loading={loading}
            onView={(membre) => { setSelectedMembre(membre); setShowDetailModal(true); }}
            onViewDocuments={async (membre) => { setSelectedMembre(membre); setShowDocumentsModal(true); await loadMembreDocuments(membre.id); }}
            onCreateEstimation={(membre) => { setSelectedMembre(membre); setShowEstimationPickerModal(true); }}
            onFTPEAction={(membre) => { setSelectedMembre(membre); setShowFTPEModal(true); }}
            onMemberUpdated={(updatedMembre) => {
              setMembres(prev => prev.map(m => m.id === updatedMembre.id ? { ...m, ...updatedMembre } : m));
            }}
            onImpersonate={['admin', 'super_admin'].includes(userRole) ? handleImpersonate : null}
            onRefresh={fetchData}
            onDelete={userRole === 'super_admin' ? async (membre) => {
              const mid = membre.id || membre.numero_membre;
              if (!mid) { toast.error('Identifiant membre manquant'); return; }
              if (!window.confirm(`Supprimer le membre ${membre.prenom || ''} ${membre.nom || ''} ? Cette action est irreversible.`)) return;
              try {
                await axios.delete(`${BACKEND_URL}/api/members/admin/${mid}`, { headers: getAuthHeader() });
                toast.success(`Membre ${membre.nom} supprime`);
                setMembres(prev => prev.filter(m => (m.id || m.numero_membre) !== mid));
              } catch (error) {
                const detail = error.response?.data?.detail;
                toast.error(typeof detail === 'string' ? detail : 'Erreur lors de la suppression');
              }
            } : null}
            showFTPE={true}
            showRCDS={true}
            showEntreprise={true}
            showExportCSV={userRole === 'super_admin'}
            userRole={userRole}
            emptyMessage="Aucun membre trouvé"
            emptySubMessage={membres.length === 0
              ? "Cliquez sur 'Nouveau Membre' pour créer un membre"
              : "Aucun résultat pour votre recherche"}
          />
        </CardContent>
      </Card>

      <MembreCreationForm
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        onSuccess={fetchData}
        getAuthHeader={getAuthHeader}
        creatorRole={userRole}
      />

      {/* Modals */}
      <DetailModal
        open={showDetailModal}
        onClose={setShowDetailModal}
        membre={selectedMembre}
        onCreateSouscription={() => { setShowDetailModal(false); setShowSouscriptionModal(true); }}
      />
      <SouscriptionModal
        open={showSouscriptionModal}
        onClose={setShowSouscriptionModal}
        membre={selectedMembre}
        coopers={coopers}
        selectedCooper={selectedCooper}
        setSelectedCooper={setSelectedCooper}
        onSubmit={handleCreateSouscription}
      />
      <EstimationSimulatorPickerModal
        open={showEstimationPickerModal}
        onClose={setShowEstimationPickerModal}
        membre={selectedMembre}
      />
      <DocumentsModal
        open={showDocumentsModal}
        onClose={setShowDocumentsModal}
        membre={selectedMembre}
        membreDocuments={membreDocuments}
        loadingDocuments={loadingDocuments}
        onRegenerated={() => { if (selectedMembre) loadMembreDocuments(selectedMembre.id); }}
      />
      <FTPEModal
        open={showFTPEModal}
        onClose={setShowFTPEModal}
        membre={selectedMembre}
        ftpeLoading={ftpeLoading}
        onAction={handleFTPEAction}
      />
    </div>
  );
}
